/**
 * EstateGates — Estate accounts.
 *
 * Ported from SPFx ProceduralGates:
 *   - _validateRequiredFields (Estate branch)
 *   - _validateEntityRestrictions (ESTATE_NO_ODP, ESTATE_NO_DEBIT)
 *   - _validateRelationships (ESTATE_PERSONAL_REP)
 *   - _validateDocumentCollection (ESTATE_COURT_DOCS)
 */

import type { CartSnapshot, GateResult } from '../workflow-engine/types';
import {
  buildCipGate,
  buildFeatureRestrictionGates,
  buildOfacGate,
  buildRequiredFieldGates,
  buildRiskReviewGate,
  getByRole,
  getPrimary,
  productHasDebit,
  productHasOdp,
  type FieldSpec,
} from '../ProceduralGates';

const ESTATE_FIELDS: ReadonlyArray<FieldSpec> = [
  { field: 'name2', label: 'Estate name' },
  { field: 'taxId', label: 'EIN' },
  { field: 'addr', label: 'Street address' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'zip', label: 'ZIP code' },
];

export function runEstateGates(cart: CartSnapshot): GateResult[] {
  const gates: GateResult[] = [];
  const primary = getPrimary(cart);
  if (!primary) return gates;

  gates.push(...buildRequiredFieldGates(cart, primary, ESTATE_FIELDS));

  // Restrictions — no ODP, no debit card
  gates.push(
    ...buildFeatureRestrictionGates(cart, [
      {
        gateCode: 'ESTATE_NO_ODP',
        label: 'Estate cannot have ODP',
        predicate: productHasOdp,
        reason: 'Estate accounts cannot have Overdraft Protection.',
      },
      {
        gateCode: 'ESTATE_NO_DEBIT',
        label: 'Estate cannot have debit card',
        predicate: productHasDebit,
        reason: 'Estate accounts cannot have a debit card.',
      },
    ]),
  );

  // Personal representative required (SPFx ESTATE_PERSONAL_REP)
  const personalReps = getByRole(cart, 'signer');
  if (personalReps.length === 0) {
    gates.push({
      gateCode: 'ESTATE_PERSONAL_REP',
      entityType: cart.entityType ?? '',
      category: 'relationship',
      status: 'fail',
      label: 'Personal representative required',
      reason: 'Estate accounts require a personal representative signer.',
      remediationAction: { label: 'Add representative', deepLinkStepId: 'people' },
    });
  }

  // Court docs on file (SPFx ESTATE_COURT_DOCS)
  if (!primary.probateDocOnFile) {
    gates.push({
      gateCode: 'ESTATE_COURT_DOCS',
      entityType: cart.entityType ?? '',
      category: 'documentCollection',
      status: 'fail',
      label: 'Letters Testamentary / Administration on file',
      reason: 'Estate accounts require court documents (Letters Testamentary or of Administration) on file.',
      remediationAction: { label: 'Upload court docs', deepLinkStepId: 'compliance' },
    });
  }

  // Court order review — Risk Management soft touchpoint. Status driven by
  // cart.riskReviewStatus (RiskReviewFunction sets it at pipeline step 7→8).
  gates.push(buildRiskReviewGate(
    cart,
    'COURT_ORDER_REVIEW',
    'Court order reviewed by Risk Management',
    'compliance',
  ));

  gates.push(buildCipGate(cart));
  gates.push(buildOfacGate(cart));

  // Escheatment jurisdiction (v3 new gate — estates must confirm state law).
  gates.push({
    gateCode: 'ESCHEATMENT_JURISDICTION',
    entityType: cart.entityType ?? '',
    category: 'compliance',
    status: 'warn',
    label: 'Escheatment jurisdiction confirmed',
    reason: 'Pending: capture jurisdiction override when estate crosses state lines.',
  });

  return gates;
}
